import React, { useEffect, useState, useRef } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import useParams from 'core/hooks/useParams'
import { getResources, listResourceInventory, updateResourceInventory } from '../actions'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import ExternalLink from 'core/components/ExternalLink'
import Button from 'core/elements/button/Button'
import {
  CPU_ALLOCATION_RATIO_TOOLTIP,
  EPHEMERAL_DISK_ALLOCATION_RATIO_TOOLTIP,
  MEMORY_ALLOCATION_RATIO_TOOLTIP,
} from './helpers'
import SimpleLink from 'core/components/SimpleLink'

const defaultParams = {
  VCPU: '',
  MEMORY_MB: '',
  DISK_GB: '',
}

const DEFAULT_ALLOCATION_RATIOS = {
  VCPU: '16',
  MEMORY_MB: '1.5',
  DISK_GB: '9999',
}

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    maxWidth: 'none',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  resetToDefault: {
    textAlign: 'right',
  },
}))

// Helper to filter resources and return UUIDs
const getResourceUUIDsByHosts = (resources, hosts) => {
  const filteredHostByName = hosts?.map((host) => host?.hypervisor?.hypervisor_hostname)
  return resources
    ?.filter((resource) => filteredHostByName?.includes(resource?.name))
    ?.map((resource) => resource?.uuid)
}

export default function AllocationRatioModal({ rows: hosts, onClose }) {
  const classes = useStyles()
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const [resources, setResources] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [inventories, setInventories] = useState({})
  const [resourceGenerations, setResourceGenerations] = useState({})
  const [loadingInventories, setLoadingInventories] = useState(true)
  const [isDefaultParams, setIsDefaultParams] = useState(true)

  // 🔹 Keep refs
  const originalParamsRef = useRef(defaultParams)
  const touchedRef = useRef(false)

  // Fetch resources once
  useEffect(() => {
    const getAllResources = async () => {
      const resources = await getResources()
      setResources(resources)
    }

    getAllResources()
  }, [])

  // Fetch inventories using UUIDs
  useEffect(() => {
    const fetchInventories = async () => {
      const resourceUUIDs = getResourceUUIDsByHosts(resources, hosts)
      if (resourceUUIDs?.length) {
        setLoadingInventories(true)
        const results = await Promise.all(
          resourceUUIDs.map((uuid) => listResourceInventory({ id: uuid })),
        )

        // merge inventories and track generations
        const mergedInventories = results.reduce((acc, curr) => {
          return { ...acc, ...curr.inventories }
        }, {})

        const generations = results.reduce((acc, curr, idx) => {
          acc[resourceUUIDs[idx]] = curr.resource_provider_generation
          return acc
        }, {})

        setInventories(mergedInventories)
        setResourceGenerations(generations)

        // Pre-fill params with allocation ratios
        const newParams = Object.keys(defaultParams).reduce((acc, key) => {
          acc[key] = mergedInventories[key]?.allocation_ratio ?? ''
          return acc
        }, {} as typeof defaultParams)

        setParams(newParams)
        originalParamsRef.current = newParams // 🔹 baseline set here
        touchedRef.current = false // reset touched when refetched
        setIsDefaultParams(true) // fresh state
        setLoadingInventories(false)
      }
    }
    fetchInventories()
  }, [resources, hosts, setParams])

  // Track changes vs original baseline
  useEffect(() => {
    if (loadingInventories) return

    const changed = Object.keys(params).some(
      (key) => String(params[key]) !== String(originalParamsRef.current[key]),
    )

    // directly set state based on comparison
    setIsDefaultParams(!changed)
  }, [params, loadingInventories])

  const handleResetToDefault = () => {
    setParams(DEFAULT_ALLOCATION_RATIOS)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    const filterResourcesUUIDByHostName = getResourceUUIDsByHosts(resources, hosts)

    const allocationRatioPromises = filterResourcesUUIDByHostName?.map(async (uuid) => {
      const filteredInventories = Object.keys(inventories).reduce((accum, current) => {
        return {
          ...accum,
          [current]: {
            ...inventories[current],
            allocation_ratio: !!params[current]
              ? Number(params[current])
              : inventories[current].allocation_ratio,
          },
        }
      }, {})

      return updateResourceInventory({
        id: uuid,
        body: {
          inventories: filteredInventories,
          resource_provider_generation: resourceGenerations[uuid],
        },
      })
    })

    const data = await Promise.allSettled(allocationRatioPromises)
    if (data) onClose()
    setSubmitting(false)
  }

  return (
    <ModalForm
      title={'Update Allocation Ratios'}
      loading={loadingInventories}
      onClose={onClose}
      panel="dialog"
      open
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={submitting || isDefaultParams}
            loading={submitting}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className={classes.container}>
        <Text variant="body2">
          Allocation ratios define how your CPU, Memory and Ephemeral Disk resources should be
          over-committed.{' '}
          <ExternalLink
            url={
              'https://platform9.com/docs/private-cloud-director/private-cloud-director/add-hosts-Virtualized-Cluster#host-overcommitment--allocation-ratios'
            }
          >
            Info
          </ExternalLink>
        </Text>
        <TextField
          id="cpu"
          label="CPU Allocation Ratio"
          onChange={getParamsUpdater('VCPU')}
          info={CPU_ALLOCATION_RATIO_TOOLTIP}
          value={params.VCPU}
          type="number"
          className={classes.fields}
        />
        <TextField
          id="memory"
          label="Memory Allocation Ratio"
          onChange={getParamsUpdater('MEMORY_MB')}
          info={MEMORY_ALLOCATION_RATIO_TOOLTIP}
          value={params.MEMORY_MB}
          type="number"
          className={classes.fields}
        />
        <TextField
          id="disk"
          label="Ephemeral disk allocation ratio"
          onChange={getParamsUpdater('DISK_GB')}
          info={EPHEMERAL_DISK_ALLOCATION_RATIO_TOOLTIP}
          value={params.DISK_GB}
          type="number"
          className={classes.fields}
        />
        <SimpleLink className={classes.resetToDefault} onClick={handleResetToDefault}>
          {'Reset to default'}
        </SimpleLink>
      </div>
    </ModalForm>
  )
}
